import axios from "axios";
import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import "flatpickr/dist/flatpickr.min.css";
import VMasker from "vanilla-masker";
import { showAlert } from "../utils/alert.js";
import { requestFcmToken, onForegroundMessage } from "../utils/firebase.js";

export default (tenantSlug) => ({
  slug: tenantSlug,
  step: 1, // 1: Serviço, 2: Profissional, 3: Data/Hora, 4: Dados, 5: Sucesso
  isLoading: true,
  isFetchingSlots: false,
  isSubmitting: false,
  isSearchingClient: false,

  services: [],
  professionals: [],
  availableSlots: [],

  form: {
    service_id: "",
    user_id: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    customer_phone: "",
    customer_name: "",
    customer_email: "",
    push_optin: false,
    cf_turnstile_response: "",
    fcm_token: null,
    device_info: null,
  },

  selected: {
    service_name: "",
    service_price: "",
    professional_name: "",
  },

  // Adicione esta função ao seu objeto (no mesmo nível do initWizard)
  setTurnstileToken(token) {
    this.form.cf_turnstile_response = token;
  },

  async initWizard() {
    await this.fetchInitialData();
    this.initCalendar();

    // ESCUTA O EVENTO DISPARADO PELO CLOUDFLARE
    window.addEventListener("turnstile-success", (event) => {
      this.form.cf_turnstile_response = event.detail;
    });

    // Aguarda o DOM renderizar o Passo 4 (mesmo estando escondido) para aplicar a máscara
    this.$nextTick(() => {
      this.initPhoneMask();
    });

    // ==========================================
    // FCM: ESCUTA NOTIFICAÇÕES EM PRIMEIRO PLANO PARA O CLIENTE
    // ==========================================
    this.listenForClientNotifications();
  },

  // NOVA FUNÇÃO: Tratamento visual da notificação para a aba ativa
  listenForClientNotifications() {
    onForegroundMessage((payload) => {
      console.log("Foreground message received (Client):", payload);

      // Utiliza o SweetAlert/Toast nativo da sua aplicação
      showAlert(payload.notification.body, "info", { timer: 3000 });
    });
  },

  async fetchInitialData() {
    try {
      const [resServices, resUsers] = await Promise.all([
        axios.get(`/api/public/${this.slug}/services`),
        axios.get(`/api/public/${this.slug}/professionals`),
      ]);

      this.services = resServices.data.data;
      this.professionals = resUsers.data.data;
      this.isLoading = false;
    } catch (error) {
      console.error("Erro ao carregar dados da barbearia", error);
      showAlert("Barbearia temporariamente indisponível.", "error");
    }
  },

  initCalendar() {
    flatpickr("#public-calendar", {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      defaultDate: this.form.date,
      minDate: "today",
      inline: true,
      onChange: (selectedDates, dateStr) => {
        this.form.date = dateStr;
        this.checkAvailability();
      },
    });
  },

  initPhoneMask() {
    const phoneInput = document.getElementById("customer_phone_input");
    if (phoneInput) {
      VMasker(phoneInput).maskPattern("(99) 99999-9999");
    }
  },

  /**
   * Smart Search: Triggered by @input in Blade
   * Fetches client data by phone number to auto-fill the form.
   */
  async searchClientByPhone() {
    const rawPhone = this.form.customer_phone.replace(/\D/g, "");

    if (rawPhone.length >= 10) {
      this.isSearchingClient = true;
      try {
        const response = await axios.get(
          `/api/public/${this.slug}/clients/search`,
          {
            params: { phone: rawPhone },
          },
        );

        const clientData = response.data.data;

        // If client is found, hydrate the form
        if (clientData) {
          if (clientData.name) {
            this.form.customer_name = clientData.name;
          }

          // Bulletproof check for booleans coming from APIs
          if (
            clientData.push_optin !== undefined &&
            clientData.push_optin !== null
          ) {
            this.form.push_optin =
              clientData.push_optin == 1 || clientData.push_optin === true;
          }
        } else {
          this.form.customer_name = "";
          this.form.push_optin = false;
        }
      } catch (error) {
        // O feedback visual do erro 429 (Too Many Requests) agora é tratado
        // automaticamente pelo interceptor global do Axios.
        // Aqui apenas ignoramos o 429 ou logamos erros inesperados.
        if (!error.response || error.response.status !== 429) {
          console.error("Erro ao buscar cliente", error);
        }
      } finally {
        this.isSearchingClient = false;
      }
    }
  },

  // Navegação
  selectService(service) {
    this.form.service_id = service.id;
    this.selected.service_name = service.name;
    this.selected.service_price = service.price;
    this.nextStep();
  },

  selectProfessional(prof) {
    this.form.user_id = prof.id;
    this.selected.professional_name = prof.name;
    this.checkAvailability();
    this.nextStep();
  },

  selectTime(time) {
    this.form.time = time;
  },

  nextStep() {
    if (this.step < 5) this.step++;
  },

  prevStep() {
    if (this.step > 1) {
      this.step--;
      if (this.step === 3) this.form.time = "";
    }
  },

  async checkAvailability() {
    this.form.time = "";
    this.availableSlots = [];
    this.isFetchingSlots = true;

    try {
      const response = await axios.get(
        `/api/public/${this.slug}/availability`,
        {
          params: {
            user_id: this.form.user_id,
            service_id: this.form.service_id,
            date: this.form.date,
          },
        },
      );
      this.availableSlots = response.data.data;
    } catch (error) {
      console.error("Erro ao verificar horários", error);
    } finally {
      this.isFetchingSlots = false;
    }
  },

  async submitBooking() {
    if (this.form.customer_phone === "") {
      showAlert("Por favor, insira seu telefone.", "warning");
      document.getElementById("customer_phone_input").focus();
      return;
    }

    if (this.form.customer_name === "") {
      showAlert("Por favor, insira seu nome.", "warning");
      document.getElementById("customer_name_input").focus();
      return;
    }

    if (this.form.customer_email != "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.form.customer_email)) {
        showAlert("Por favor, insira um e-mail válido.", "warning");
        document.getElementById("customer_email_input").focus();
        return;
      }
    }

    if (!this.form.cf_turnstile_response) {
      showAlert(
        "Aguarde a verificação de segurança antes de prosseguir.",
        "warning",
      );
      return;
    }

    // =========================================================================
    // FCM PERMISSION & TOKEN LOGIC
    // =========================================================================
    if (this.form.push_optin) {
      try {
        // 1. Check if the browser supports notifications
        if ("Notification" in window) {
          // 2. Request permission explicitly during the submit user gesture
          let permission = Notification.permission;

          if (permission !== "granted") {
            permission = await Notification.requestPermission();
          }

          // 3. If granted, proceed to fetch the token
          if (permission === "granted") {
            this.form.fcm_token = await requestFcmToken();
            this.form.device_info = navigator.userAgent;
          } else {
            // Fallback: If the user denies at the OS level, uncheck the opt-in to avoid backend errors
            console.warn("Notification permission was denied by the user.");
            this.form.push_optin = false;
            this.form.fcm_token = null;
            this.form.device_info = null;
          }
        } else {
          // Browser does not support Notifications
          this.form.push_optin = false;
        }
      } catch (e) {
        console.warn("Error during notification permission request", e);
        this.form.push_optin = false;
      }
    }

    this.isSubmitting = true;
    try {
      await axios.post(`/api/public/${this.slug}/appointments`, this.form);
      this.step = 5;
    } catch (error) {
      if (window.turnstile) {
        window.turnstile.reset();
        this.form.cf_turnstile_response = "";
      }

      // Handle Validation Errors from Laravel (HTTP 422)
      if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data.errors;
        const firstErrorKey = Object.keys(validationErrors)[0];
        const errorMessage = validationErrors[firstErrorKey][0];

        showAlert(errorMessage, "warning");
        return;
      }

      // Handle Scheduling Conflicts (HTTP 409)
      if (error.response && error.response.status === 409) {
        const errorMessage = error.response.data.error || "Horário Esgotado";
        showAlert(errorMessage, "warning");

        this.prevStep();
        this.checkAvailability();
      } else {
        // Fallback for 500s or network issues
        showAlert("Não foi possível concluir o agendamento.", "error");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  formatMoney(value) {
    return parseFloat(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },
});
